export type AnalyticsSource = "typed" | "menu" | "link" | "boot";

type GtagFn = (...args: unknown[]) => void;

declare global {
    interface Window {
        gtag?: GtagFn;
    }
}

const COMMAND_MAX_LEN = 100;

export type AnalyticsEvent =
    | "boot_start"
    | "boot_complete"
    | "press_any_key"
    | "command"
    | "ui_click"
    | "file_download"
    | "command_error"
    | "clear_screen"
    | "reboot"
    | "password_prompt"
    | "password_cancel";

export function trackEvent(
    name: AnalyticsEvent,
    params?: Record<string, string | number | boolean>,
): void {
    // gtag may be absent (ad blockers / offline); never affect the terminal.
    window.gtag?.("event", name, params);
}

function truncate(value: string): string {
    const trimmed = value.trim();
    return trimmed.length <= COMMAND_MAX_LEN ? trimmed : trimmed.slice(0, COMMAND_MAX_LEN);
}

function firstWord(value: string): string {
    return value.trim().split(/\s+/)[0]?.toLowerCase() ?? "";
}

/** GA command event — separate from local `/api/command` telemetry. */
export function trackGaCommand(command: string, source: AnalyticsSource): void {
    const expanded = truncate(command);
    const cmd = firstWord(expanded);
    if (!cmd) return;

    trackEvent("command", { cmd, command: expanded, source });
}

export function trackUiClick(
    target: "menu" | "link",
    label: string,
    command: string,
): void {
    trackEvent("ui_click", {
        target,
        label: truncate(label),
        command: truncate(command),
    });
}

export function trackFileDownload(filename: string): void {
    trackEvent("file_download", { filename: truncate(filename) });
}

export function trackCommandError(cmd: string, reason: string): void {
    trackEvent("command_error", { cmd, reason });
}
